import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Lock, ShieldCheck, Landmark, LineChart, PieChart, Sparkles,
} from "lucide-react";
import { GainBadge } from "../components/ui";
import Logo from "../components/Logo";
import TierBadge from "../components/TierBadge";
import Avatar from "../components/Avatar";
import Disclaimer from "../components/Disclaimer";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";
import usePricing, { planPrice } from "../lib/pricing";
import { price as fmtPrice } from "../lib/format";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import LegalLinks from "../components/LegalLinks";

/**
 * The public front door. Renders entirely logged-out — this is what a stranger and
 * a crawler see, so it carries the proposition rather than a login box.
 *
 * Co Stock is a free tracker for your own investments. The investor showcase and the
 * pricing cards are still here, but only render while their product area is open
 * (backend/features.py); while closed, one short "what's next" card mentions the
 * investor idea as a plan rather than as something you can use today.
 */
function Section({ children, className = "" }) {
  return <section className={`max-w-5xl mx-auto px-5 ${className}`}>{children}</section>;
}

export default function Home() {
  const { t } = useTranslation();
  const { features } = useAuth();
  const [investors, setInvestors] = useState([]);
  // usePricing asks the server, which answers 404 while payments are off; the cards
  // that would show its numbers are not rendered then either.
  const pricing = usePricing({ enabled: features.payments });
  const proPrice = planPrice(pricing, "pro");

  useSeo({
    title: t("home.seoTitle"),
    description: t("home.seoDesc"),
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Co Stock",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web, iOS, Android",
      description: t("home.seoDesc"),
      // Free is a fact the page states, so the markup says it too. A price in
      // structured data that disagrees with the page is worse than none.
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    },
  });

  useEffect(() => {
    if (!features.community) return;
    api.publicLeaderboard().then((r) => setInvestors((r.data.investors || []).slice(0, 3))).catch(() => {});
  }, [features.community]);

  const freeFeatures = ["f1", "f2", "f3", "f4", "f5"];
  const proFeatures = ["p1", "p2", "p3", "p4", "p5"];

  return (
    <div className="bg-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Logo size={30} /></Link>
          <nav className="flex items-center gap-2 sm:gap-4 text-sm">
            {features.community && (
              <>
                <Link to="/leaderboard" className="text-slate-600 hover:text-slate-900 hidden sm:block">
                  {t("nav.leaderboard")}
                </Link>
                <Link to="/for-investors" className="text-slate-600 hover:text-slate-900 hidden sm:block">
                  {t("home.forInvestors")}
                </Link>
              </>
            )}
            <Link to="/login" className="text-slate-600 hover:text-slate-900">{t("auth.signIn")}</Link>
            <Link to="/register" className="btn-primary py-2 px-4" data-testid="home-cta-top">
              {t("home.getStarted")}
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <Section className="pt-16 pb-14 sm:pt-24 sm:pb-20 text-center">
        <span className="inline-flex items-center gap-1.5 pill bg-brand-50 text-brand-700">
          <ShieldCheck size={13} /> {t("home.badge")}
        </span>
        <h1 className="mt-5 text-4xl sm:text-6xl font-heading font-semibold tracking-tighter leading-[1.05]">
          {t("home.h1a")}{" "}<br className="hidden sm:block" />
          <span className="text-brand-600">{t("home.h1b")}</span>
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">{t("home.sub")}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/register" className="btn-primary text-base py-3 px-6 justify-center" data-testid="home-cta-hero">
            {t("home.ctaFree")} <ArrowRight size={17} />
          </Link>
          {features.community ? (
            <Link to="/leaderboard" className="btn-outline text-base py-3 px-6 justify-center">
              {t("home.ctaBrowse")}
            </Link>
          ) : (
            <a href="#features" className="btn-outline text-base py-3 px-6 justify-center">
              {t("home.ctaFeatures")}
            </a>
          )}
        </div>
        <p className="mt-3 text-xs text-slate-400">{t("home.noCard")}</p>
      </Section>

      {/* Proof: real investors, real numbers — only while the community is open. */}
      {features.community && investors.length > 0 && (
        <Section className="pb-16">
          <div className="rounded-3xl border border-slate-200 p-5 sm:p-7">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold tracking-tight">{t("home.topInvestors")}</h2>
              <Link to="/leaderboard" className="text-sm text-brand-600 hover:underline">
                {t("common.viewAll")}
              </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {investors.map((inv) => (
                <Link key={inv.investor_id} to={`/investors/${inv.investor_id}`}
                      className="card p-4 hover:shadow-soft transition-shadow">
                  <div className="flex items-center gap-3">
                    <Avatar src={inv.avatar} name={inv.display_name} size={40} />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{inv.display_name}</div>
                      {inv.username && <div className="text-xs text-slate-400 truncate">@{inv.username}</div>}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <TierBadge tier={inv.tier} size="xs" />
                    {inv.return_pct != null && <GainBadge value={inv.return_pct} />}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* How it works */}
      <Section className="py-16 border-t border-slate-100">
        <h2 className="text-3xl font-heading font-semibold tracking-tighter text-center">{t("home.howTitle")}</h2>
        <div className="mt-10 grid sm:grid-cols-3 gap-8">
          {[["track", LineChart], ["analyse", PieChart], ["tax", Landmark]].map(([k, Icon], i) => (
            <div key={k}>
              <div className="w-11 h-11 rounded-2xl bg-brand-50 text-brand-600 grid place-items-center">
                <Icon size={20} strokeWidth={1.8} />
              </div>
              <div className="mt-4 text-xs font-semibold text-slate-400">0{i + 1}</div>
              <h3 className="mt-1 font-heading font-semibold text-lg tracking-tight">{t(`home.step.${k}.t`)}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t(`home.step.${k}.b`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* What you get. Everything is free; the list is the whole product. */}
      {!features.payments && (
        <Section className="py-16 border-t border-slate-100">
          <div id="features" className="text-center scroll-mt-24">
            <h2 className="text-3xl font-heading font-semibold tracking-tighter">{t("home.featuresTitle")}</h2>
            <p className="mt-3 text-slate-600">{t("home.featuresSub")}</p>
          </div>
          <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3.5 max-w-3xl mx-auto" data-testid="home-features">
            {freeFeatures.map((k) => (
              <li key={k} className="flex items-start gap-2.5 text-sm sm:text-base text-slate-700">
                <Check size={17} className="text-emerald-500 shrink-0 mt-0.5" /> {t(`home.free.${k}`)}
              </li>
            ))}
          </ul>
          <div className="mt-10 text-center">
            <Link to="/register" className="btn-primary text-base py-3 px-6 inline-flex" data-testid="home-cta-features">
              {t("home.ctaFree")} <ArrowRight size={17} />
            </Link>
          </div>
        </Section>
      )}

      {/* Pricing — only while payments are switched on. */}
      {features.payments && (
      <Section className="py-16 border-t border-slate-100">
        <div className="text-center">
          <h2 className="text-3xl font-heading font-semibold tracking-tighter">{t("home.pricingTitle")}</h2>
          <p className="mt-3 text-slate-600">{t("home.pricingSub")}</p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <div className="card p-6" data-testid="plan-free">
            <h3 className="font-heading font-semibold text-xl tracking-tight">{t("home.freeName")}</h3>
            <div className="mt-2 text-4xl font-heading font-semibold tracking-tight">
              {fmtPrice(0, pricing?.currency || "EUR")}
              <span className="text-base font-normal text-slate-400">{t("home.perMonth")}</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">{t("home.freeDesc")}</p>
            <ul className="mt-5 space-y-2.5">
              {freeFeatures.map((k) => (
                <li key={k} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check size={15} className="text-emerald-500 shrink-0 mt-0.5" /> {t(`home.free.${k}`)}
                </li>
              ))}
            </ul>
            <Link to="/register" className="btn-outline w-full justify-center mt-6" data-testid="plan-free-cta">
              {t("home.ctaFree")}
            </Link>
          </div>

          <div className="card p-6 relative ring-2 ring-brand-500" data-testid="plan-pro">
            <span className="absolute -top-3 left-6 pill bg-brand-600 text-white">{t("home.popular")}</span>
            <h3 className="font-heading font-semibold text-xl tracking-tight">{t("home.proName")}</h3>
            <div className="mt-2 text-4xl font-heading font-semibold tracking-tight">
              {/* No fallback literal: showing a stale price confidently is the bug
                  this whole change exists to remove, so an unloaded price is blank. */}
              {proPrice === null
                ? <span className="inline-block w-24 h-9 align-middle rounded-lg bg-slate-100 animate-pulse" />
                : fmtPrice(proPrice, pricing?.currency || "EUR")}
              <span className="text-base font-normal text-slate-400">{t("home.perMonth")}</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">{t("home.proDesc")}</p>
            <ul className="mt-5 space-y-2.5">
              {proFeatures.map((k) => (
                <li key={k} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check size={15} className="text-brand-500 shrink-0 mt-0.5" /> {t(`home.pro.${k}`)}
                </li>
              ))}
            </ul>
            <Link to="/register" className="btn-primary w-full justify-center mt-6" data-testid="plan-pro-cta">
              {t("home.ctaPro")}
            </Link>
            <p className="mt-3 text-[11px] text-slate-400 text-center">
              {t("home.vatNote", { vat: pricing?.charged_vat_pct ?? pricing?.vat_pct ?? 19 })}
            </p>
          </div>
        </div>
      </Section>
      )}

      {/* The investor idea, as a plan. Gone once the community actually opens, because
          then the real thing is on this page instead. */}
      {!features.community && (
        <Section className="py-16 border-t border-slate-100">
          <div className="rounded-3xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8 flex flex-col sm:flex-row gap-4 sm:gap-6 items-start" data-testid="home-next">
            <div className="w-11 h-11 rounded-2xl bg-white text-brand-600 grid place-items-center shrink-0 border border-slate-200">
              <Sparkles size={20} strokeWidth={1.8} />
            </div>
            <div>
              <span className="label">{t("home.nextLabel")}</span>
              <h2 className="mt-1 font-heading font-semibold text-xl tracking-tight">{t("home.nextTitle")}</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">{t("home.nextBody")}</p>
            </div>
          </div>
        </Section>
      )}

      {/* Trust */}
      <Section className="py-16 border-t border-slate-100">
        <div className="grid sm:grid-cols-3 gap-8">
          {[["privacy", Lock], ["integrity", ShieldCheck], ["tax", Landmark]].map(([k, Icon]) => (
            <div key={k}>
              <Icon size={19} className="text-brand-500" />
              <h3 className="mt-3 font-heading font-semibold tracking-tight">{t(`home.trust.${k}.t`)}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t(`home.trust.${k}.b`)}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-brand-600 to-indigo-700 text-white p-8 sm:p-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter">{t("home.finalTitle")}</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">{t("home.finalSub")}</p>
          <Link to="/register" className="btn-invert mt-7 inline-flex text-base py-3 px-6">
            {t("home.ctaFree")} <ArrowRight size={17} />
          </Link>
        </div>
      </Section>

      <Section className="pb-10"><Disclaimer /></Section>

      <footer className="border-t border-slate-100 py-8">
        <Section className="flex flex-wrap items-center justify-between gap-4 text-sm text-slate-400">
          <span>&copy; {new Date().getFullYear()} Co Stock</span>
          <div className="flex flex-wrap gap-4">
            {features.community && (
              <>
                <Link to="/for-investors" className="hover:text-brand-600">{t("home.forInvestors")}</Link>
                <Link to="/leaderboard" className="hover:text-brand-600">{t("nav.leaderboard")}</Link>
              </>
            )}
            <LegalLinks />
          </div>
        </Section>
      </footer>
    </div>
  );
}
